import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { bandColor } from "@/components/seo/seoUtils";
import { cmsPreviewRequest, seoPagePreviewRequest } from "@/components/seo/seoPreview";

/**
 * Live SEO score and problems while the editor is still typing.
 * Uses the same scorer the server stores on save and on publish.
 */
export default function SeoFormScore({ resource, values, page }) {
  const request = useMemo(
    () => (page ? seoPagePreviewRequest(page) : cmsPreviewRequest(resource, values)),
    [page, resource, values],
  );
  const requestKey = useMemo(() => JSON.stringify(request), [request]);
  const [preview, setPreview] = useState(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setPending(true);
    const timer = setTimeout(() => {
      api
        .post("/seo/preview", JSON.parse(requestKey))
        .then((res) => {
          if (!cancelled) setPreview(res.data || null);
        })
        .catch(() => {
          if (!cancelled) setPreview(null);
        })
        .finally(() => {
          if (!cancelled) setPending(false);
        });
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [requestKey]);

  const score = preview?.seo_score;
  const issues = Array.isArray(preview?.issues) ? preview.issues : [];

  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 space-y-3">
      <div className="flex items-center gap-3">
        <div
          className="w-12 h-12 rounded-full border border-white/10 flex items-center justify-center text-sm font-semibold"
          style={{ color: bandColor(score) }}
          title="SEO score from the current form"
        >
          {pending && score == null ? "…" : score ?? "—"}
        </div>
        <div>
          <p className="text-sm text-white">SEO score for this form</p>
          <p className="text-[11px] text-white/45">
            Updates as you type. Saving a draft or publishing stores this same score.
          </p>
        </div>
      </div>
      {issues.length === 0 && score != null ? (
        <p className="text-xs text-emerald-200/80">No SEO problems in the current draft.</p>
      ) : (
        <ul className="space-y-2">
          {issues.map((issue) => (
            <li key={issue.rule_id} className="text-xs text-white/75">
              <span className="uppercase tracking-wide text-[10px] text-amber-200/80 mr-2">{issue.severity}</span>
              {issue.message}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
